import type {
  FetchVatDefinitionListParams,
  VATDefinition
} from 'Services/ProductManagement/Features/VATDefinition/types'
import { createBaseApi } from 'Util/ApiClient'
import { RTKCacheTags } from 'Util/constants'
import { formatToTimeZoneAwareISO } from 'Util/formatters'

enum TagTypes {
  VAT_DEFINITION = 'VATDefinition',
  ACTIVE_VAT_DEFINITION = 'ActiveVATDefinition'
}

export const vatDefinitionApi = createBaseApi({
  reducerPath: 'vatDefinitionApi',
  keepUnusedDataFor: 5,
  tagTypes: [TagTypes.VAT_DEFINITION, TagTypes.ACTIVE_VAT_DEFINITION]
}).injectEndpoints({
  endpoints: builder => ({
    // biome-ignore lint/suspicious/noConfusingVoidType: RTK query requires void type for no params
    getVatDefinitions: builder.query<VATDefinition[], FetchVatDefinitionListParams | void>({
      query: params => ({
        url: '/api/v1/vat-definition/',
        params: params ?? undefined
      }),
      providesTags: result =>
        result
          ? [
              ...result.map(({ id }) => ({ type: TagTypes.VAT_DEFINITION, id }) as const),
              { type: TagTypes.VAT_DEFINITION, id: RTKCacheTags.LIST }
            ]
          : [{ type: TagTypes.VAT_DEFINITION, id: RTKCacheTags.LIST }],
      transformResponse: (response: { resources: VATDefinition[] }) => response.resources ?? []
    }),
    getActiveVatDefinitions: builder.query<
      VATDefinition[],
      // biome-ignore lint/suspicious/noConfusingVoidType: RTK query requires void type for no params
      FetchVatDefinitionListParams | void
    >({
      query: params => {
        const currentDateTime = formatToTimeZoneAwareISO(new Date())
        return {
          url: '/api/v1/vat-definition/',
          params: {
            ...params,
            filter: `activeStart le "${currentDateTime}" and activeEnd ge "${currentDateTime}"`
          }
        }
      },
      providesTags: result =>
        result
          ? [
              ...result.map(({ id }) => ({ type: TagTypes.ACTIVE_VAT_DEFINITION, id }) as const),
              { type: TagTypes.ACTIVE_VAT_DEFINITION, id: RTKCacheTags.LIST }
            ]
          : [{ type: TagTypes.ACTIVE_VAT_DEFINITION, id: RTKCacheTags.LIST }],
      transformResponse: (response: { resources: VATDefinition[] }) => response.resources
    }),
    getSingleVatDefinition: builder.query<VATDefinition, string>({
      query: id => `/api/v1/vat-definition/${id}/`,
      providesTags: (_result, _error, id) => [{ type: TagTypes.VAT_DEFINITION, id }]
    }),
    createSingleVatDefinition: builder.mutation<VATDefinition, Partial<VATDefinition>>({
      query: data => ({
        url: `/api/v1/vat-definition/`,
        method: 'POST',
        body: data
      }),
      invalidatesTags: [
        { type: TagTypes.VAT_DEFINITION, id: RTKCacheTags.LIST },
        { type: TagTypes.ACTIVE_VAT_DEFINITION, id: RTKCacheTags.LIST }
      ]
    }),
    updateSingleVatDefinition: builder.mutation<
      VATDefinition,
      { id: string } & Partial<VATDefinition>
    >({
      query: data => {
        const { id, ...fields } = data
        return {
          url: `/api/v1/vat-definition/${id}/`,
          method: 'PUT',
          body: fields
        }
      },
      invalidatesTags: (_result, _error, { id }) => [
        { type: TagTypes.VAT_DEFINITION, id },
        { type: TagTypes.ACTIVE_VAT_DEFINITION, id }
      ]
    }),
    deleteSingleVatDefinition: builder.mutation<Record<string, never>, { id: string }>({
      query: ({ id }) => ({
        url: `/api/v1/vat-definition/${id}/`,
        method: 'DELETE'
      }),
      invalidatesTags: () => [
        { type: TagTypes.VAT_DEFINITION, id: RTKCacheTags.LIST },
        { type: TagTypes.ACTIVE_VAT_DEFINITION, id: RTKCacheTags.LIST }
      ]
    })
  })
})

export const {
  useGetVatDefinitionsQuery,
  useGetActiveVatDefinitionsQuery,
  useCreateSingleVatDefinitionMutation,
  useUpdateSingleVatDefinitionMutation,
  useDeleteSingleVatDefinitionMutation,
  useGetSingleVatDefinitionQuery
} = vatDefinitionApi
